import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Eye, Heart, Handshake, Home, Sparkles, AlertTriangle, Factory, CalendarCheck, CalendarClock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { useT, fmtRelative } from '../salesI18n';
import { useSalesNotifications, notifTarget, notifMessage, notifDetail } from '../hooks/useSalesNotifications';

const ICON = { viewed: Eye, interest: Heart, accepted: Handshake, estimate: Home, simulation: Sparkles, project_stage: Factory, project_scheduled: CalendarCheck, project_rescheduled: CalendarClock };
const TONE = { viewed: 'bg-sky-50 text-sky-700 border-sky-100', interest: 'bg-rose-50 text-rose-600 border-rose-100', accepted: 'bg-emerald-50 text-emerald-700 border-emerald-100', estimate: 'bg-amber-50 text-amber-700 border-amber-100', simulation: 'bg-teal-50 text-teal-700 border-teal-100', project_stage: 'bg-amber-50 text-amber-700 border-amber-100', project_scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-100', project_rescheduled: 'bg-indigo-50 text-indigo-700 border-indigo-100' };

const Item = ({ n, onOpen, t, lang }) => {
  const Icon = n.kind === 'simulation' && n.status === 'failed' ? AlertTriangle : ICON[n.kind] || Bell;
  const detail = notifDetail(n);
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={`notif-item-${n.id}`} data-read={n.read ? '1' : '0'}
      className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-2xl transition-colors hover:bg-white ${n.read ? 'opacity-70' : 'bg-teal-50/50'}`}>
      <span className={`mt-0.5 h-8 w-8 shrink-0 rounded-xl border flex items-center justify-center ${TONE[n.kind] || 'bg-slate-50 text-slate-600 border-slate-100'}`}><Icon className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className={`block text-[13px] leading-snug text-slate-800 ${n.read ? 'font-medium' : 'font-semibold'}`}>{notifMessage(t, n)}</span>
        {detail && <span className="block text-[11px] text-slate-500 mt-0.5 truncate">{detail}</span>}
        <span className="block text-[10px] text-slate-400 mt-0.5 tabular-nums">{fmtRelative(n.created_at, lang)}</span>
      </span>
      {!n.read && <span className="mt-2 h-2 w-2 rounded-full bg-teal-600 shrink-0" data-testid={`notif-unread-dot-${n.id}`} />}
    </button>
  );
};

export const NotificationBell = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { inbox, markRead, markAllRead, refreshInbox } = useSalesNotifications();
  const [open, setOpen] = useState(false);
  const onOpen = (n) => { markRead(n); setOpen(false); navigate(notifTarget(n)); };
  const badge = inbox.unread > 99 ? '99+' : inbox.unread;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) refreshInbox(); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="sales-notif-bell" aria-label={t.notifications} className="relative inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/70 border border-white/80 text-slate-700 hover:bg-white transition-colors">
          <Bell className="h-4 w-4" />
          {inbox.unread > 0 && <span data-testid="sales-notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">{badge}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="w-[min(92vw,380px)] p-0 rounded-3xl border border-white/90 sales-glass-strong shadow-xl" data-testid="sales-notif-panel">
        <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
          <p className="font-bold text-sm text-slate-900">{t.notifications}{inbox.unread > 0 && <span className="ml-2 text-[11px] font-semibold text-teal-700">{inbox.unread} {t.unread}</span>}</p>
          <button type="button" onClick={markAllRead} disabled={inbox.unread === 0} data-testid="sales-notif-read-all" className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900 disabled:opacity-40 disabled:cursor-default">
            <CheckCheck className="h-3.5 w-3.5" /> {t.markAllRead}
          </button>
        </div>
        <div className="max-h-[min(60vh,420px)] overflow-y-auto px-1.5 pb-2 space-y-0.5" data-testid="sales-notif-list">
          {inbox.items.length === 0 ? (
            <div className="px-4 py-8 text-center" data-testid="sales-notif-empty">
              <Bell className="h-6 w-6 mx-auto text-slate-300" />
              <p className="text-xs text-slate-500 mt-2">{t.notifEmpty}</p>
            </div>
          ) : inbox.items.map((n) => <Item key={`${n.kind}-${n.id}`} n={n} onOpen={onOpen} t={t} lang={lang} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
};
